import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import { ICreateExpenseData } from "../../@types";
import { DownloadIcon } from "../../assets/icons";
import { CheckboxComponent } from "../core/Forms";
import InfoIcon from "../../assets/icons/InfoIcon";
import ArrowRight from "../../assets/icons/ArrowRight";
import ArrowLeft from "../../assets/icons/ArrowLeft";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { DotMenu } from "../../assets/icons/DotMenu";
// @ts-ignore
import NewAttachIcon from "../../assets/icons/NewAttach";
import NewEditIcon from "../../assets/icons/NewEdit";
import NewDeleteIcon from "../../assets/icons/NewDelete";

import { formatMoney } from "../../utils";
import { useUserStore } from "../../state";
import AnnotationComponent from "../common/Annotation/AnnotationComponent";
import { GroupButton } from "../common/GroupButtons";
import { isNotAccountant } from "../../utils/isNotAccountant";
import EditIcon from "../../assets/icons/EditIcon";
import AttachIcon from "../../assets/icons/AttachIcon";
import DeleteIcon from "../../assets/icons/DeleteIcon";
import useAnnotations from "../../hooks/annotation/useAnnotations";
import { useExpense } from "../../hooks";

interface expenseDetailsProp {
  expense: ICreateExpenseData;
  isBillable: boolean;
  isProtected: boolean;
  downloading: boolean;
  handleIsProtectedChange: () => void;
  handleDownloadClick: () => void;
  expenseId: string; // Add expenseId to props
  setShowUploadReceipt: (show: boolean) => void; // Add setShowUploadReceipt to props
  setShowPrompt: (show: boolean) => void; // Add setShowPrompt to props
}



export const ExpenseDetails: React.FC<expenseDetailsProp> = ({
  expense,
  isBillable,
  isProtected,
  downloading,
  handleIsProtectedChange,
  handleDownloadClick,
  expenseId,
  setShowUploadReceipt,
  setShowPrompt,
}) => {
  const { t } = useTranslation("expenses");
  const [openDotMenu, setOpenDotMenu] = useState(false);
  const navigate = useNavigate();
   const { roles } = useUserStore();
  const isAdmin = roles.includes("Admin");
  const {  annotations } = useExpense(expenseId);
   const annotationsData = useAnnotations(annotations, "Expense");
   

  const handleEditExpense = () => {
    navigate(`/expenses/edit/${expenseId}`);
    setOpenDotMenu(false);
  };

  const handleAttachReceipt = () => {
    setShowUploadReceipt(true);
    setOpenDotMenu(false);
  };

  const handleDeleteExpense = () => {
    setShowPrompt(true);
    setOpenDotMenu(false);
  };

  const groupButtonProps = useMemo(
    () => ({
      buttons: [ 
        {
          text: "Edit",
          icon: <NewEditIcon />,
          href: `/expenses/edit/${expenseId}`,
          textColorClass: "text-[#344054]",
        },
        {
          text: `Attach Receipt`,
          icon: <NewAttachIcon />,
          onClick: () => setShowUploadReceipt(true),
          textColorClass: "text-[#344054]",
        },
        {
          text: "Delete",
          icon: <NewDeleteIcon />,
          onClick: () => setShowPrompt(true),
          textColorClass: "text-[#FB0303]",
          fontSize: "text-[#FB0303]",
        },
      ].filter(Boolean),
    }),
    [downloading, expenseId, handleDownloadClick],
  );
  const MobileGroupButtonProps = useMemo(
    () => ({
      buttons: [
        {
          text: "Edit",
          icon: <EditIcon />,
          href: `/expenses/edit/${expenseId}`,
          fontSize: "text-[12px]",
        },
        {
          text: downloading ? 'Downloading...' : 'Download',
          icon: <DownloadIcon className="text-gray-700" />,
          fontSize: 'text-[12px]',
          onClick: handleDownloadClick,
        },
      ].filter(Boolean),
    }),
    [downloading, expenseId, handleDownloadClick],
  );

  return (
    <>
      <div className="bg-white shadow-sm rounded-[8px] border">
        <div className="md:p-8 px-4 pb-8 pt-4 flex flex-col md:flex-row justify-between items-start md:items-center">
          <div>
            <h3 className="font-semibold text-[18px] text-[#212121]">
              {expense.vendorName || "NA"}
            </h3>
            <p className="text-[14px] text-[#808080] mt-1">
              Created: {dayjs(expense?.date).format("MM/DD/YYYY")}
            </p>
          </div>
          <div className="flex justify-between w-full md:w-auto  items-center gap-4 font-semibold md:text-[20px] mt-4 md:mt-0">
            <div className="flex flex-col gap-2">
              <h3 className="font-medium text-gray-600 md:text-[14px] leading-6">
                {t("budget.expenseAmount")}
              </h3>
              <span className="bg-[#FEF3F2] text-[#E02523] p-2 rounded-[4px] ">{`${formatMoney(expense?.amount)}`}</span>
            </div>
 
            
          </div>
        </div>

        <hr className="my-[-1em]" />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6 md:px-8 px-4">
          <div>
            <h4 className="text-gray-600 text-[14px] font-medium">
              Payment method
            </h4>
            <p className="text-[#212121] text-[16px] mt-2 capitalize">
              {expense?.paymentMethod}
            </p>
          </div>
          <div>
            <h4 className="text-gray-600 text-[14px] font-medium">
              Billed to
            </h4>
            <p className="text-[#212121] text-[16px] mt-2">
              {expense?.customerName ? expense?.customerName : "N/A"}
            </p>
          </div>
          <div>
            <h4 className="text-gray-600 text-[14px] font-medium">
              Expense category
            </h4>
            <p className="text-[#212121] text-[16px] mt-2">
              {expense?.category}
            </p>
          </div>
          <div>
            <h4 className="text-gray-600 text-[14px] font-medium">
              Reference
            </h4>
            <p className="text-[#212121] text-[16px] mt-2">
              {expense?.reference}
            </p>
          </div>
          <div className="col-span-2 lg:col-span-1">
            <h4 className="text-gray-600 text-[14px] font-medium">Notes</h4>
            <p className="text-[#212121] text-[16px] mt-2">
              {expense?.notes ? expense?.notes : "N/A"}
            </p>
          </div>

          {expense?.receiptUrl && (
            <div>
              <h3 className="text-gray-600 md:text-[16px] leading-5">
                Attachment
              </h3>
              <div className="flex gap-6 text-[#044E97] mt-3">
                <a target="_blank" href={expense?.receiptUrl}>
                  <p>View Receipt</p>
                </a>

                <div
                  onClick={handleDownloadClick}
                  className="flex items-center justify-center rounded-[4px] gap-1 w-[113px] h-[27px] "
                >
                  <p>
                    {downloading ? (
                      "Downloading..."
                    ) : (
                      <div>
                        Download
                        {/* <DownloadIcon /> */}
                      </div>
                    )}
                  </p>
                  <DownloadIcon />
                </div>
              </div>
            </div>
          )}
        </div>

          <div className="flex justify-between items-center mt-6 border-t pr-4">
            <div>
          {isAdmin && (
               <div className="flex items-center gap-6  border-[#044E97] border-opacity-10 py-4 md:px-8 px-4">
               <div className="flex items-center gap-2 cursor-pointer">
                 <span className="text-[14px] font-medium">
                   Mark as protected expense
                 </span>
               </div>
               <div
                 onClick={handleIsProtectedChange} // Make the entire switch clickable
                 className={`w-[44px] h-[24px] rounded-full flex items-center transition duration-200 ease-in ${
                   isProtected
                     ? "bg-[#044E97] justify-end"
                     : "bg-[#F2F4F7] justify-start"
                 }  cursor-pointer`}
               >
                 <div
                   className={`${
                     isProtected ? "bg-[#ffffff]" : "bg-[#ffffff]"
                   } w-5 h-5 rounded-full `}
                 />
               </div>
             </div>
          )}
          </div>

          

           </div>
      </div>
    </>
  );
};
